import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { CurrentUser } from "../../../common/providers/current-user";
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { SessionRights } from '../../providers/session-rights';
import { LibraryService } from '../../../libraries/providers/library.services';
import { Subscription } from 'rxjs';
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { Organization } from "../../../common/models/organization";
import { Session } from "../../../common/models/session";
import { Library } from "../../../common/models/library";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { NgIf } from "@angular/common";
import { LibrariesSideMenuComponent } from "../../../libraries/components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { FlexLayoutModule } from "@angular/flex-layout";
import { SessionsSessionViewComponent } from "../../components/session-view/session-view";

@Component({
  selector: 'sessions-session-page',
  providers: [],
  standalone: true,
  styleUrls: ['./session.scss'],
  imports: [
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    NgIf,
    LibrariesSideMenuComponent,
    Error401Component,
    FlexLayoutModule,
    TranslateModule,
    SessionsSessionViewComponent
  ],
  templateUrl: './session.html'
})
export class SessionsSessionPageComponent implements OnInit, OnDestroy {
    public pageTitle = 'Athlete View';
    public is401 = false;
    public is404 = false;
    public hasLibrary = true;

    public organization: Organization = {} as Organization;
    public session: Session =  {} as Session;
    public rights: SessionRights = new SessionRights(this._utils);
    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        private api: ApiBaseService,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private libraryService: LibraryService,
        private _title: Title,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _route: ActivatedRoute,
        private _translater: TranslateService
    ) {
    }

    public ngOnInit(): void {
        this._route.data.subscribe((data: any) => { // { organization: Organization; session: Session }
            this.organization = data.organization;
            this.session = data.session;
            this.createPageTitle();
            this.createBreadcrumb();

            this.rights.evaluateScreenRights({
                rolesIds: this._currentUserRoles.getRolesIds(this.organization.id!)
            });

            if (!this.session) {
                this.is404 = true;
                return;
            }

            if (!this.rights.LIST) {
                this.is401 = true;
                return;
            }
        });
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    private createPageTitle() {
        this.pageTitle = this.session.name ?? '';
    }

    private createBreadcrumb() {

        let firstCrumb: Crumb = {} as Crumb;
        this.libraryService.getByOrganization(this.organization).subscribe({
            next: (library: Library | null) => {
              if (!library) {
                  this.hasLibrary = false;
                  return;
              }
              this.subscriptions.push(
                  this._route.data.subscribe((data:any) => {
                      // Detect the current route context
                      const currentPath = this._route.snapshot.url.map(segment => segment.path).join('/');
                      const libraryId = this.session.libraryId ? this.session.libraryId : library.id;
                      let listUrl: string;
                      let detailUrl: string;
                      let categoryTitle: string;

                      if (currentPath.includes('sessions-elitment')) {
                          // Elitment sessions context
                          categoryTitle = this._translater.instant('Elitment_sessions_title');
                          this._title.setTitle(this.pageTitle + ' | ' + categoryTitle + ' | ' + this.conf.platformName);
                          listUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-elitment';
                          detailUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-elitment/' + this.session.id;
                          firstCrumb = {
                              shortTitle: categoryTitle,
                              title: categoryTitle,
                              url: listUrl,
                          };
                      } else if (currentPath.includes('sessions-super-coach')) {
                          // Super Coach sessions context
                          categoryTitle = this._translater.instant('Super_coaches_sessions_title');
                          this._title.setTitle(this.pageTitle + ' | ' + categoryTitle + ' | ' + this.conf.platformName);
                          listUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-super-coach';
                          detailUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-super-coach/' + this.session.id;
                          firstCrumb = {
                              shortTitle: categoryTitle,
                              title: categoryTitle,
                              url: listUrl,
                          };
                      } else {
                          // Personal sessions context (default for old routes)
                          categoryTitle = this._translater.instant('Personal_sessions_title');
                          this._title.setTitle(this.pageTitle + ' | ' + categoryTitle + ' | ' + this.conf.platformName);
                          listUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions/personal';
                          detailUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions/personal/' + this.session.id;
                          firstCrumb = {
                              shortTitle: categoryTitle,
                              title: categoryTitle,
                              url: listUrl,
                          };
                      }

                      const crumbs: Crumb[] = [
                          firstCrumb,
                          {
                              shortTitle: this.session.name ??'',
                              title: this.session.name ??'',
                              url: detailUrl
                          }
                      ];

                      this.breadcrumbProvider.setDefaultCrumbs(crumbs);
                  })
              );
            }
        });
    }
}
